/**
 * Notes de suivi — la mémoire écrite du CRM (objet natif Twenty `note`).
 * Toute information orale (appel, visite, consigne d'atelier) doit pouvoir
 * atterrir ici en dix secondes, sinon elle se perd.
 */
import { z } from "zod";
import { type EmailEquipe, PRENOMS_EQUIPE, type Role } from "./roles";

/** Types du composer (§9.4) — « Appel » alimente le journal du module Appels. */
export const TYPES_SUIVI = ["Note", "Appel", "Tâche", "Rappel", "Compte rendu", "Incident"] as const;
export type TypeSuivi = (typeof TYPES_SUIVI)[number];

/**
 * « Tâche » et « Rappel » ne sont pas des notes : ils créent une vraie tâche
 * Twenty (assignable, avec échéance), sinon le bouton mentirait sur son nom.
 */
export function estTache(type: TypeSuivi): boolean {
  return type === "Tâche" || type === "Rappel";
}

export const LIMITE_NOTE = 4000;

export const AjoutNoteSchema = z.object({
  type: z.enum(TYPES_SUIVI).default("Note"),
  texte: z.string().trim().min(1, "note vide").max(LIMITE_NOTE, "note trop longue"),
});
export type AjoutNote = z.infer<typeof AjoutNoteSchema>;

export const NoteCreeeSchema = z.object({
  ok: z.literal(true),
  noteId: z.string(),
  titre: z.string(),
  /** vrai si une tâche a été créée plutôt qu'une note (types Tâche / Rappel) */
  tache: z.boolean().default(false),
});
export type NoteCreee = z.infer<typeof NoteCreeeSchema>;

/**
 * Titre lisible dans Twenty comme dans la chronologie du CRM :
 * « Appel · 30/07/2026 · Élodie ». Le corps garde le texte intégral.
 */
export function titreNote(type: TypeSuivi, quand: Date, auteur: string | null): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const date = `${p(quand.getDate())}/${p(quand.getMonth() + 1)}/${quand.getFullYear()}`;
  return auteur ? `${type} · ${date} · ${nomLisible(auteur)}` : `${type} · ${date}`;
}

/**
 * « elodie.geceram@gmail.com » → « Élodie », « cfao.geceram@… » → « CFAO ».
 * Un compte de l'équipe prend son prénom dans `PRENOMS_EQUIPE` (accents,
 * sigles, et jamais un nom de famille tiré de l'adresse) ; toute autre
 * adresse garde la règle générale : segments du local capitalisés.
 */
export function nomLisible(email: string): string {
  const prenom = PRENOMS_EQUIPE[email.trim().toLowerCase() as EmailEquipe];
  if (prenom !== undefined) return prenom;
  // Sigles de service listés explicitement : une règle sur la longueur
  // transformerait « dove » en « DOVE ».
  const SIGLES = new Set(["cfao", "sav", "adv", "cad", "labo"]);
  const local = (email.split("@")[0] ?? "").replace(/\.geceram$/i, "");
  return local
    .split(".")
    .filter(Boolean)
    .map((mot) => (SIGLES.has(mot.toLowerCase()) ? mot.toUpperCase() : mot.charAt(0).toUpperCase() + mot.slice(1)))
    .join(" ");
}

/** Écrire une note est un geste d'équipe : tous les rôles opérationnels y ont droit. */
export function canEcrireNote(roles: readonly Role[]): boolean {
  return roles.some((r) =>
    (
      ["admin", "secretariat", "commercial", "comptabilite", "logistique", "production", "technicien"] as readonly Role[]
    ).includes(r),
  );
}
